import {productImpressionCart} from '@optimaxdev/analytics/desktop/lib/cart/productImpressionCart';
import {SagaIterator} from 'redux-saga';
import {
    put,
    takeEvery,
    call,
    select,
    takeLatest,
    cancel,
    take,
    fork,
    all,
    putResolve,
} from 'redux-saga/effects';

import {CartItemType} from 'desktop/pages/cart/types';

import {PAYPAL_EVENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {dispatchCustomEvent} from 'libs/events/events';
import {localStore} from 'libs/storage/storage';
import {getUrlSearchParam} from 'libs/url/url';

import {cartUpdated, getCartQuoteSaga, productAdded} from '../../actions';
import {
    addAccessory,
    fetchAccessories as fetchAccessoriesAction,
} from '../../reducers/accessories/accessories';
import {AccessoryWindowType} from '../../reducers/accessories/accessoriesType';
import {addUpsellFrameToCart} from '../../reducers/addCartItemUpsell/addCartItemUpsell';
import {AddCartItemUpsellType} from '../../reducers/addCartItemUpsell/addCartItemUpsellType';
import {setMessages} from '../../reducers/messages/messages';
import {getPayment} from '../../reducers/payments/payments';
import {getQuote, getSavedQuote, removeQuoteItem} from '../../reducers/quoteItems/quoteItems';

import {addCartItemUpsell, getCartAccessories, getPayments, getQuoteItems} from '../../selectors';

import {default as cartAnalyticsSaga} from './analytics/analytics';

/**
 * Get offer frames
 */
export function* getOfferFrames() {
    const items: CartItemType[] = yield select(getQuoteItems);
    const firstItem = items[0];

    if (firstItem && 'offerFrames' in firstItem && firstItem.offerFrames?.length) {
        const options = {
            category: firstItem.offerFrames && firstItem.offerFrames[0].category,
            list: 'Cart upsell 2nd pair',
        };
        yield call(productImpressionCart, firstItem.offerFrames, options);
    }
}

/**
 * Add upsell frame to cart
 */
export function* addUpsellItem() {
    // @ts-expect-error
    const quote = yield call(getQuote);
    yield putResolve(quote);
    const getUpsells: AddCartItemUpsellType = yield select(addCartItemUpsell);
    yield put(setMessages(getUpsells.messages));
}

/**
 * Get cart data
 */
export function* getCartData() {
    const cartIdFromUrl: string = yield call(getUrlSearchParam, 'cartid');
    const tokenFromUrl: string = yield call(getUrlSearchParam, 'token');
    if (cartIdFromUrl || tokenFromUrl) {
        yield putResolve(getSavedQuote(cartIdFromUrl || tokenFromUrl));
    } else {
        yield putResolve(getQuote({ids: []}));
    }

    yield call(getOfferFrames);
    if (localStore.get('cartUpdated')) {
        yield all([put(cartUpdated()), call([localStore, 'remove'], 'cartUpdated')]);
    } else if (localStore.get('cartAdded')) {
        yield all([put(productAdded()), call([localStore, 'remove'], 'cartAdded')]);
    }
}

/**
 * Starts initialization process of PayPal payment button
 */
export function* initPayPal(): SagaIterator {
    const payments = yield select(getPayments);
    if (!payments.payPal) yield cancel();

    yield call(dispatchCustomEvent, PAYPAL_EVENTS.READY_PAYPAL_CART);
}

/**
 * Update cart quote
 */
export function* updateQuote() {
    // @ts-expect-error
    const quote = yield call(getQuote);
    yield putResolve(quote);
}

/**
 * Fetch accessories info if there is any
 */
export function* fetchAccessories() {
    const accessoriesPrev: AccessoryWindowType[] = yield select(getCartAccessories);
    const prevIds = accessoriesPrev.map(({id}: AccessoryWindowType) => Number(id));
    yield take([
        `${getQuote.fulfilled}`,
        `${getQuote.rejected}`,
        `${removeQuoteItem.fulfilled}`,
        `${removeQuoteItem.rejected}`,
    ]);
    const accessoriesCurrent: AccessoryWindowType[] = yield select(getCartAccessories);
    const currentIds = accessoriesCurrent.map(({id}: AccessoryWindowType) => Number(id));

    /* the isCartPage selector returns true for the CTP, because its URL starts with 'checkout/cart',
    and a request for accessories is useless there */
    const shouldFetchAccessories = currentIds.length > 0;
    if (currentIds.some((id: number) => !prevIds.includes(id)) || shouldFetchAccessories)
        yield put(fetchAccessoriesAction(currentIds));
}

/**
 * Begin of saga
 */
export default function* cartQuoteSaga() {
    yield fork(cartAnalyticsSaga);
    yield takeEvery(`${getCartQuoteSaga}`, getCartData);
    yield takeEvery(`${addUpsellFrameToCart.fulfilled}`, addUpsellItem);
    yield takeEvery([`${getQuote.pending}`, `${removeQuoteItem.pending}`], fetchAccessories);
    yield takeEvery(`${addAccessory.fulfilled}`, updateQuote);
    yield takeLatest([`${getQuote.fulfilled}`, `${getPayment.fulfilled}`], initPayPal);
}
